import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Link as LinkIcon,
  Calculator,
  BarChart3,
  FolderOpen,
  Settings,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  LogIn,
  MessageSquare,
  MapPin,
  Combine,
} from 'lucide-react';
import { getDocs, query, orderBy, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/services/firebase';
import { useBusiness } from '@/contexts/BusinessContext';
import { usePageTour, WelcomeModal } from '@/walkthrough';
import { adminDashboardPageTour } from '@/walkthrough/tourSteps';
import { formatDistanceToNow } from 'date-fns';
import {
  commissionRulesCollection,
  syncStatusCollection,
} from '@/config/collections';

// ===========================================
// Admin Dashboard Page
// ===========================================

interface ConfigStatus {
  rulesConfigured: boolean;
  lastSync: Date | null;
}

interface ActivityItem {
  id: string;
  type: 'partner_login' | 'partner_note' | 'partner_referral_update' | 'visit_logged';
  timestamp: Date;
  description: string;
  userId?: string;
  userName?: string;
  companyId?: string;
  companyName?: string;
  projectId?: string;
  projectName?: string;
}

/**
 * Safely parse a timestamp from the Cloud Function response.
 * Firebase callable serializes Date/Timestamp objects in various formats.
 */
function parseTimestamp(value: any): Date {
  if (value instanceof Date) return value;
  // ISO string: "2026-01-31T22:21:47.890Z"
  if (typeof value === 'string') {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d;
  }
  // Firebase Timestamp wire format: {_seconds, _nanoseconds} or {seconds, nanoseconds}
  if (typeof value === 'object' && value !== null) {
    const seconds = value._seconds ?? value.seconds;
    if (typeof seconds === 'number') return new Date(seconds * 1000);
  }
  // Epoch milliseconds
  if (typeof value === 'number') return new Date(value);
  return new Date();
}

export function AdminDashboardPage() {
  const { currentBusiness, getBusinessLabel } = useBusiness();
  const { showWelcomeModal, startTour, skipTour, globalOptOut } = usePageTour(adminDashboardPageTour);
  const [configStatus, setConfigStatus] = useState<ConfigStatus>({
    rulesConfigured: false,
    lastSync: null,
  });
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(true);

  useEffect(() => {
    async function checkConfigStatus() {
      try {
        // Check commission rules (using namespaced path)
        const rulesSnapshot = await getDocs(commissionRulesCollection(db));
        const rulesConfigured = !rulesSnapshot.empty;

        // Check last sync from Portal (most recent project update)
        const syncQuery = query(
          syncStatusCollection(db),
          orderBy('completedAt', 'desc'),
          limit(1)
        );
        const syncSnapshot = await getDocs(syncQuery);
        let lastSync: Date | null = null;
        if (!syncSnapshot.empty) {
          const syncData = syncSnapshot.docs[0].data();
          if (syncData.completedAt) {
            lastSync = syncData.completedAt.toDate();
          }
        }

        setConfigStatus({
          rulesConfigured,
          lastSync,
        });
      } catch (error) {
        console.error('Error checking config status:', error);
      } finally {
        setLoading(false);
      }
    }

    async function fetchRecentActivity() {
      try {
        const functions = getFunctions();
        const getActivity = httpsCallable<{ limit?: number }, { activities: ActivityItem[] }>(
          functions,
          'crm_getRecentActivity'
        );
        const result = await getActivity({ limit: 20 });

        const activitiesWithDates = result.data.activities.map(activity => ({
          ...activity,
          timestamp: parseTimestamp(activity.timestamp),
        }));

        setActivities(activitiesWithDates);
      } catch (error) {
        console.error('Error fetching activity:', error);
      } finally {
        setLoadingActivity(false);
      }
    }

    checkConfigStatus();
    fetchRecentActivity();
  }, []);

  const refreshActivity = async () => {
    setLoadingActivity(true);
    try {
      const functions = getFunctions();
      const getActivity = httpsCallable<{ limit?: number }, { activities: ActivityItem[] }>(
        functions,
        'crm_getRecentActivity'
      );
      const result = await getActivity({ limit: 20 });

      const activitiesWithDates = result.data.activities.map(activity => ({
        ...activity,
        timestamp: parseTimestamp(activity.timestamp),
      }));

      setActivities(activitiesWithDates);
    } catch (error) {
      console.error('Error refreshing activity:', error);
    } finally {
      setLoadingActivity(false);
    }
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-600 mt-1">
          Manage {getBusinessLabel(currentBusiness)} configuration and view system status.
        </p>
      </div>

      {/* Configuration status */}
      <div data-tour="system-status" className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Configuration Status</h2>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="animate-spin text-blue-600" size={24} />
            <span className="ml-2 text-gray-600">Checking configuration...</span>
          </div>
        ) : (
        <div className="space-y-4">
          <StatusItem
            label="Commission Rules"
            status={configStatus.rulesConfigured ? 'success' : 'warning'}
            message={
              configStatus.rulesConfigured
                ? 'Rules configured for all project types'
                : 'No commission rules configured'
            }
            link="/admin/rules"
          />
          <StatusItem
            label="Last Portal Sync"
            status={configStatus.lastSync ? 'success' : 'pending'}
            message={
              configStatus.lastSync
                ? `Last synced from Portal: ${configStatus.lastSync.toLocaleString()}`
                : 'No sync has occurred yet'
            }
          />
        </div>
        )}
      </div>

      {/* Admin quick actions */}
      <div data-tour="admin-action-cards" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <AdminCard
          title="Users & Reps"
          description="Manage user accounts and permissions"
          icon={<Users size={32} />}
          link="/admin/users"
          color="blue"
        />
        <AdminCard
          title="Partner Access"
          description="Manage partner portal tokens and access"
          icon={<LinkIcon size={32} />}
          link="/admin/partners"
          color="purple"
        />
        <AdminCard
          title="Commission Rules"
          description="Set up commission calculations"
          icon={<Calculator size={32} />}
          link="/admin/rules"
          color="green"
          badge={!configStatus.rulesConfigured ? 'Setup Required' : undefined}
        />
        <AdminCard
          title="Reports"
          description="View all commission reports"
          icon={<BarChart3 size={32} />}
          link="/admin/reports"
          color="yellow"
        />
        <AdminCard
          title="Evidence Library"
          description="View all partner compensation evidence"
          icon={<FolderOpen size={32} />}
          link="/admin/evidence"
          color="red"
        />
        <AdminCard
          title="System Diagnostics"
          description="View logs and run maintenance tasks"
          icon={<Settings size={32} />}
          link="/admin/diagnostics"
          color="gray"
        />
        <AdminCard
          title="Merge Companies"
          description="Find and combine duplicate referral sources"
          icon={<Combine size={32} />}
          link="/admin/merge-companies"
          color="yellow"
        />
      </div>

      {/* Recent Activity */}
      <div data-tour="admin-recent-activity" className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
        {loadingActivity ? (
          <div className="text-center py-8 text-gray-500">
            <RefreshCw size={48} className="mx-auto mb-3 opacity-50 animate-spin" />
            <p>Loading activity...</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock size={48} className="mx-auto mb-3 opacity-50" />
            <p>No recent activity</p>
            <p className="text-sm">Partner logins, notes, and visits will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => (
              <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  {activity.type === 'partner_login' && <LogIn size={16} className="text-blue-600" />}
                  {activity.type === 'partner_note' && <MessageSquare size={16} className="text-green-600" />}
                  {activity.type === 'visit_logged' && <MapPin size={16} className="text-purple-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{activity.description}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    {activity.companyName && <span>{activity.companyName}</span>}
                    {activity.userName && <span>• {activity.userName}</span>}
                    <span>• {formatDistanceToNow(activity.timestamp, { addSuffix: true })}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <button
          onClick={refreshActivity}
          className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          disabled={loadingActivity}
        >
          <RefreshCw size={16} className={loadingActivity ? 'animate-spin' : ''} />
          Refresh Activity
        </button>
      </div>

      <WelcomeModal
        pageName={adminDashboardPageTour.pageName}
        onStartTour={startTour}
        onSkip={skipTour}
        onGlobalOptOut={globalOptOut}
        open={showWelcomeModal}
      />
    </div>
  );
}

// ===========================================
// Status Item Component
// ===========================================

interface StatusItemProps {
  label: string;
  status: 'success' | 'warning' | 'error' | 'pending';
  message: string;
  link?: string;
}

function StatusItem({ label, status, message, link }: StatusItemProps) {
  const statusIcons = {
    success: <CheckCircle className="text-green-500" size={20} />,
    warning: <AlertTriangle className="text-yellow-500" size={20} />,
    error: <AlertTriangle className="text-red-500" size={20} />,
    pending: <Clock className="text-gray-400" size={20} />,
  };

  const content = (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3">
        {statusIcons[status]}
        <div>
          <p className="font-medium text-gray-900">{label}</p>
          <p className="text-sm text-gray-600">{message}</p>
        </div>
      </div>
      {link && (
        <span className="text-blue-600 text-sm hover:text-blue-800">Configure &rarr;</span>
      )}
    </div>
  );

  return link ? <Link to={link}>{content}</Link> : content;
}

// ===========================================
// Admin Card Component
// ===========================================

interface AdminCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
  color: 'blue' | 'green' | 'purple' | 'yellow' | 'red' | 'gray';
  badge?: string;
}

function AdminCard({ title, description, icon, link, color, badge }: AdminCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200',
    green: 'bg-green-50 text-green-600 hover:bg-green-100 border-green-200',
    purple: 'bg-purple-50 text-purple-600 hover:bg-purple-100 border-purple-200',
    yellow: 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100 border-yellow-200',
    red: 'bg-red-50 text-red-600 hover:bg-red-100 border-red-200',
    gray: 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-gray-200',
  };

  return (
    <Link
      to={link}
      className={`relative block p-6 rounded-lg border transition-colors ${colorClasses[color]}`}
    >
      {badge && (
        <span className="absolute top-3 right-3 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
          {badge}
        </span>
      )}
      <div className="mb-3">{icon}</div>
      <h3 className="font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-600 mt-1">{description}</p>
    </Link>
  );
}
